import { useState, useMemo } from "react";
import MainTitle from "../../components/MainTitle";
import { useEffect } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faClock,
  faPhone,
  faUser,
  faChevronUp,
  faChevronDown,
  faArrowUpAZ,
  faArrowDownZA,
  faExclamationTriangle,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import Datepicker from "react-tailwindcss-datepicker";
import NotificationSlideOut from "../../components/NotificationSlideOut";

function Dashboard() {
  const { auth, setShowLoader } = useAuth();

  const [sortOrder, setSortOrder] = useState("asc");
  const [nextAppointment, setNextAppointment] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [overdueAppointments, setOverdueAppointments] = useState([]);
  const [patientsNotOnSemble, setPatientsNotOnSemble] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMessages, setNewMessages] = useState([]);
  const [yourClients, setYourClients] = useState([]);
  const [userDocs, setUserDocs] = useState([])
  const [value, setValue] = useState({
    startDate: moment(),
    endDate: moment().add(10, "days"),
  });
  const [days, setDays] = useState({
    startDate: moment().subtract(7, "days"),
    endDate: moment(),
  });
  const [updateDay, setUpdateDay] = useState({
    startDate: moment().subtract(7, "days"),
    endDate: moment(),
  });
  const navigate = useNavigate();
  const isGp = auth?.user?.type_id === 4 && auth?.user?.company_id === 9
  const isClinicAdmin = Boolean(auth?.user?.roles?.find(r => r.id === 8));

  const axios = useAxiosPrivate();

  const getNextAppointment = async (admin=false) => {
    setShowLoader(true);
    try {
      let response = null
      if (admin) {
        response = await axios.get(
          `/companies/${auth.user.company_id}/next-appointment`,
        );
      } else {
        response = await axios.get(
          `/users/${auth.user.id}/next-appointment`,
        );
      }
      if (!response.data.error) {
        setNextAppointment(response.data.result);
      }
    } catch (error) {}
    setShowLoader(false);
  };

  const getUpdates = async (start, end) => {
    try {
      const response = await axios.get(
        `/notifications/user-updates/${auth.user.id}/${moment(start).format("YYYY-MM-DD")}/${moment(end).format("YYYY-MM-DD")}`,
      );
      setUpdates(response.data.result);
    } catch (error) {}
  };

  const sortedClients = useMemo(() => {
    const list = Array.isArray(yourClients) ? yourClients : [];
    return [...list].sort((a, b) => {
      if (a.name < b.name) return sortOrder === "asc" ? -1 : 1;
      if (a.name > b.name) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [yourClients, sortOrder]);

  const getAppointments = async (start, end) => {
    try {
      let response = null
      if (isClinicAdmin) {
        response = await axios.get(
         `/companies/${auth.user.company_id}/sessions/${moment(start).format(
           "YYYY-MM-DD",
         )}/${moment(end).format("YYYY-MM-DD")}`,
       );
      } else {
        response = await axios.get(
         `/users/${auth.user.id}/sessions/${moment(start).format(
           "YYYY-MM-DD",
         )}/${moment(end).format("YYYY-MM-DD")}`,
       );
      }
      setAppointments(response.data.result);
    } catch (error) {}
  };

  const getTodaysAppointments = async () => {
    try {
      let response = null;
      if (isClinicAdmin) {
        response = await axios.get(
          `/companies/${auth.user.company_id}/sessions/${moment().startOf('day').format(
            "YYYY-MM-DD",
          )}/${moment().endOf('day').format("YYYY-MM-DD")}`,
        );
      } else {
        response = await axios.get(
          `/users/${auth.user.id}/sessions/${moment().startOf('day').format(
            "YYYY-MM-DD",
          )}/${moment().endOf('day').format("YYYY-MM-DD")}`,
        );
      }
      setTodaysAppointments(response.data.result);
    } catch (error) {}
  };

  const getOverdueAppointments = async () => {
    try {
      let response = null;
      if (isClinicAdmin) {
        response = await axios.get(`/companies/${auth.user.company_id}/overdue-sessions/17`);
      } else {
        response = await axios.get(`/users/${auth.user.id}/overdue-sessions/17`);
      }
      setOverdueAppointments(response.data.result);
    } catch (error) {}
  };

  const getNewMessages = async (start, end) => {
    setShowLoader(true);
    try {
      const response = await axios.get(
        `/users/${auth.user.id}/new-messages/${moment(start).format(
          "YYYY-MM-DD",
        )}/${moment(end).format("YYYY-MM-DD")}`,
      );
      if (!response.data.error) {
        setNewMessages(response.data.result);
      }
    } catch (error) {}
    setShowLoader(false);
  };

  const sortMessages = () => {
    return [...newMessages].sort((a, b) => {
      if (sortOrder === "asc") {
        return moment(a.created_at).valueOf() - moment(b.created_at).valueOf();
      } else {
        return moment(b.created_at).valueOf() - moment(a.created_at).valueOf();
      }
    });
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const getYourClients = async () => {
    setShowLoader(true);
    try {
      const response = await axios.get(`/clients/all/by-user/${auth.user.id}`);
      if (!response.data.error) {
        setYourClients(response.data.result);
      }
    } catch (error) {}
    setShowLoader(false);
  };


  const getPatientsNotOnSemble = async () => {
    setShowLoader(true);
    try {
      const response = await axios.get(`/clients/gp/not-on-semble`);
      if (!response.data.error) {
        setPatientsNotOnSemble(response.data.result);
      }
    } catch (error) {}
    setShowLoader(false);
  };

  const handleValueChange = (newValue) => {
    setValue(newValue);
    getAppointments(newValue.startDate, newValue.endDate);
  };

  const handleUpdateChange = (newValue) => {
    setUpdateDay(newValue);
    getUpdates(newValue.startDate, newValue.endDate);
  };

  const handleDayChange = (newValue) => {
    setDays(newValue);
    getNewMessages(newValue.startDate, newValue.endDate);
  };

  const viewMessage = (id, thread_reference, name) => {
    return navigate(`/clients/${id}/messages?thread=${thread_reference}`, {
      state: { name: name, id: id, reference: thread_reference },
    });
  };

  useEffect(() => {
    getYourClients();
    getNextAppointment(isClinicAdmin);
    getNewMessages(days.startDate, days.endDate);
    getAppointments(value.startDate, value.endDate, isClinicAdmin);
    getUpdates(updateDay.startDate, updateDay.endDate);
    getTodaysAppointments(isClinicAdmin);
    getOverdueAppointments(isClinicAdmin);
    if (isClinicAdmin) {
      getPatientsNotOnSemble();
    }

    const interval = setInterval(() => {
      getNextAppointment();
      if (auth?.user?.company_id === 9 && Boolean(auth?.user?.roles?.find(r => r.id === 8))) { //If gp clinic and clinic admin
        getPatientsNotOnSemble();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);


  const handleClick = (update) => {
    if (!update?.meta?.original_notification_id) {
      return;
    }

    setOpen(true);
    setSelectedThread(update);
  };

  const getUserDocs = async () => {
    try {
      const response = await axios.get(`/user/documents/${auth.user.id}`)
      if (!response.data.error) {
        setUserDocs(response.data.result)
      }
    } catch (error) {
      console.error("Failed to fetch user documents", error)
    }
  }

  const getDocIssues = () => {
    const missingCount = 6 - userDocs.length
    const expired = userDocs.filter(doc => doc.expiry_date && moment(doc.expiry_date).isBefore(moment()))
    const expiringSoon = userDocs.filter(doc => doc.expiry_date && moment(doc.expiry_date).isBetween(moment(), moment().add(30, 'days')))

    return {
      missingCount,
      expired,
      expiringSoon
    }
  }

  useEffect(() => {
    getUserDocs()
  }, [])

  return (
    <section className="scroll-smooth">
      <MainTitle title="Dashboard" />
      
      {patientsNotOnSemble?.length > 0 && (
        <div
            onClick={() => document.getElementById('new-patients')?.scrollIntoView({ behavior: 'smooth' })}
            className="flex items-center justify-center gap-2 bg-green-100 text-green-800 border border-green-300 rounded-lg px-4 py-3 font-medium mb-4 cursor-pointer hover:bg-green-200 transition"
        >
            <FontAwesomeIcon icon={faArrowDown}/>
            <p>You have new patients to enter into Semble</p>
            <FontAwesomeIcon icon={faArrowDown}/>
        </div>
      )}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Document Status Banner */}
          {/* {(() => {
            const { missingCount, expired, expiringSoon } = getDocIssues()
            if (missingCount > 0 || expired.length > 0 || expiringSoon.length > 0) {
              return (
                <article className="">
                  <div className="p-4 rounded-md bg-yellow-100 border-l-4 border-yellow-500 shadow-md">
                    <h3 className="text-yellow-800 font-semibold flex items-center mb-2">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                      <span>Action Required</span> 
                    </h3>
                    <ul className="text-sm text-yellow-700 list-disc pl-5 space-y-1">
                      {missingCount > 0 && (
                        <li>You still need to upload {missingCount} required document{missingCount > 1 ? 's' : ''}.</li>
                      )}
                      {expired.length > 0 && (
                        <li>{expired.length} document{expired.length > 1 ? 's are' : ' is'} expired and must be updated.</li>
                      )}
                      {expiringSoon.length > 0 && (
                        <li>{expiringSoon.length} document{expiringSoon.length > 1 ? 's are' : ' is'} expiring soon (within 30 days).</li>
                      )}
                    </ul>
                    <Link
                      to={`/users/${auth.user.id}`}
                      className="inline-block mt-3 rounded-lg bg-white border shadow px-4 py-2 text-sm font-medium text-blue-500 hover:shadow-lg"
                    >
                      Go to Profile & Update Documents
                    </Link>
                  </div>
                </article>
              )
            }
            return null
          })()} */}
        <article className="border-l-[5px] p-4 bg-white flex-1 border-l-wba-primary rounded-md shadow-md">
          <h3 className="text-xl font-medium mb-4 flex items-center">
            <p>Next Appointment</p>
          </h3>
          {nextAppointment && nextAppointment.id ? (
            <div className="border-t border-slate-200 py-3">
              <p className="mb-2">
                <FontAwesomeIcon
                  icon={faCalendarDays}
                  className="text-slate-400 mr-2 text-lg"
                />
                {moment(nextAppointment.start_time).format("dddd Do MMMM")}
              </p>
              <p className="mb-2">
                <FontAwesomeIcon
                  icon={faClock}
                  className="text-slate-400 mr-2 text-lg"
                />
                {moment(nextAppointment.start_time).format("H:mm")} -{" "}
                {moment(nextAppointment.end_time).format("H:mm")}
              </p>
              <p className="mb-2">
                <FontAwesomeIcon
                  icon={faUser}
                  className="text-slate-400 mr-2 text-lg"
                />
                {isGp ? nextAppointment?.additional_data?.client_name ?? nextAppointment?.client.name : nextAppointment?.client.name}
              </p>
              <p>
                <FontAwesomeIcon
                  icon={faPhone}
                  className="text-slate-400 mr-2 text-lg"
                />
                {nextAppointment.service.title}
              </p>
              <Link
                to={isGp ? `/clients/${nextAppointment.client.id}/claims/${nextAppointment.claim_id}/sessions/${nextAppointment.id}` : `/clients/${nextAppointment.client.id}/`}
                className="btn clear mt-4"
              >
                View {isGp ? 'Session' : 'Client'}
              </Link>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500">
                No Appointments Scheduled
              </p>
            </div>
          )}
        </article>

        {auth?.user?.company_id !== 9 && (
          <article className="p-4 bg-white flex-1 rounded-md shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-medium flex items-center">
                <p>Your Clients</p>
              </h3>
              <button
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                className="text-slate-500 text-sm flex items-center"
              >
                <FontAwesomeIcon
                  icon={sortOrder === "asc" ? faArrowUpAZ : faArrowDownZA}
                  className="mr-2"
                />
                Sort {sortOrder === "asc" ? "A → Z" : "Z → A"}
              </button>
            </div>

            {(yourClients ?? []).length > 0 ? (
              <ul className="max-h-[250px] min-h-[136px] md:max-h-[250px] md:min-h-[136px] overflow-y-scroll">
                {sortedClients.map((client) => (
                  <li key={client.id}>
                    <Link
                      to={`/clients/${client.id}`}
                      className="flex justify-between border-t border-slate-200 py-3 px-3 hover:bg-slate-100"
                    >
                      <div>
                        <FontAwesomeIcon
                          icon={faUser}
                          className="text-slate-400 mr-4"
                        />
                        <span>{client.name}</span>
                      </div>
                      <p>{client.reference}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">No Clients</p>
            )}
          </article>
        )}
        {auth?.user?.company_id === 9 && (
          <article className="p-2 md:p-4 bg-white flex-1 rounded-md shadow-md">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <h3 className="text-xl font-medium mb-4 flex items-center">
                <p>Today's Appointments</p>
              </h3>
            </div>
            {(todaysAppointments ?? []).length > 0 ? (
              <div>
                <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-1">
                  {todaysAppointments.map((appointment) => {
                    return (
                      <li key={appointment.id}>
                        <Link
                          to={`/clients/${appointment.client.id}/claims/${appointment.claim_id}/sessions/${appointment.id}`}
                          className="flex justify-between items-center border-t border-slate-200 py-3 px-3 hover:bg-slate-100"
                        >
                          <p className="">
                            {moment(appointment.start_time).format("HH:mm")} - {moment(appointment.end_time).format("HH:mm")}
                          </p>
                          <p>{appointment.service.title}</p>
                          <div>
                            {isClinicAdmin ? (
                              <span>{appointment?.user?.name}</span>
                            ) : (
                              <span>{isGp ? appointment?.additional_data?.client_name ?? appointment?.client?.name : appointment?.client?.name}</span>
                            )}
                          </div>
                          {appointment.status && (
                            <span
                              className={`status ${appointment.status.replace(" ", "-").toLowerCase()}`}
                            >
                              {appointment.status}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-500">No Appointments</p>
              </div>
            )}
          </article>
        )}

        <article className="p-2 md:p-4 bg-white flex-1 rounded-md shadow-md">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <h3 className="text-xl font-medium mb-4 flex items-center">
              <p>{auth?.user?.company_id === 9 && 'All'} Appointments</p>
            </h3>
            <div className=" border border-gray-300 rounded-lg p-0.5">
              <Datepicker
                primaryColor={"blue"}
                value={value}
                onChange={handleValueChange}
                useRange={false}
              />
            </div>
          </div>
          {(appointments ?? []).length > 0 ? (
            <div>
              <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-4">
                {appointments.map((appointment) => {
                  return (
                    <li key={appointment.id}>
                      <Link
                        to={`/clients/${appointment.client.id}/claims/${appointment.claim_id}/sessions/${appointment.id}`}
                        className="flex justify-between items-center border-t border-slate-200 py-3 px-3 hover:bg-slate-100"
                      >
                        <p className="">
                          {moment(appointment.start_time).format(
                            "DD/MM/YY HH:mm",
                          )}
                        </p>
                        <p>{appointment.service.title}</p>
                        <div>
                          <span>{isGp ? appointment?.additional_data?.client_name ?? appointment?.client?.name : appointment?.client?.name}</span>
                        </div>
                        {appointment.status && (
                          <span
                            className={`status ${appointment.status.replace(" ", "-").toLowerCase()}`}
                          >
                            {appointment.status}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div>
              <p className="text-sm text-slate-500">No Appointments</p>
            </div>
          )}
        </article>

        {auth?.user?.company_id === 9 && (
          <article className="p-2 md:p-4 bg-white flex-1 rounded-md shadow-md">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <h3 className="text-xl font-medium mb-4 flex items-center">
                <p>Overdue Appointments</p>
              </h3>
            </div>
            {(overdueAppointments ?? []).length > 0 ? (
              <div>
                <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-4">
                  {overdueAppointments.map((appointment) => {
                    return (
                      <li key={appointment.id}>
                        <Link
                          to={`/clients/${appointment.client.id}/claims/${appointment.claim_id}/sessions/${appointment.id}`}
                          className="flex justify-between items-center border-t border-slate-200 py-3 px-3 hover:bg-slate-100"
                        >
                          <p className="">
                            {moment(appointment.start_time).format(
                              "DD/MM/YY HH:mm",
                            )}
                          </p>
                          <p>{appointment.service.title}</p>
                          <div>
                            <span>{isGp ? appointment?.additional_data?.client_name ?? appointment?.client?.name : appointment?.client?.name}</span>
                          </div>
                          {appointment.status && (
                            <span
                              className={`status ${appointment.status.replace(" ", "-").toLowerCase()}`}
                            >
                              {appointment.status}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-500">No Appointments</p>
              </div>
            )}
          </article>
        )}
        {auth?.user?.company_id === 9 && Boolean(auth?.user?.roles?.find(r => r.id === 8)) && (
          <article className="p-2 md:p-4 bg-white flex-1 rounded-md shadow-md" id='new-patients'>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <h3 className="text-xl font-medium mb-4 flex items-center">
                <p>Patients not on Semble</p>
              </h3>
            </div>

            {(patientsNotOnSemble ?? []).length > 0 ? (
              <div>
                <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-4">
                  {patientsNotOnSemble.map((appointment) => {
                    return (
                      <li key={appointment.id}>
                        <Link
                          to={`/clients/${appointment.client_id}/claims/${appointment.claim_id}/sessions/${appointment.id}`}
                          className="flex justify-between items-center border-t border-slate-200 py-3 px-3 hover:bg-slate-100"
                        >
                          <p>
                            {moment(appointment?.start_time).format("DD/MM/YY HH:mm")}
                          </p>
                          <div>
                            <span>
                              {appointment?.client_name || 'N/A'}
                            </span>
                          </div>
                          {appointment?.status && (
                            <span
                              className={`status ${appointment.status.replace(" ", "-").toLowerCase()}`}
                            >
                              {appointment?.status}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-500">No Patients</p>
              </div>
            )}
          </article>
        )}

        {!isGp && (
          <>
            <article className="p-4 bg-white flex-1 rounded-md shadow-md">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-medium mb-4 flex items-center">
                  <p>New Messages</p>
                </h3>
                <div className="border border-gray-300 rounded-lg p-0.5 flex items-center">
                  <Datepicker
                    primaryColor={"blue"}
                    value={days}
                    onChange={handleDayChange}
                    useRange={false}
                  />
                  <button
                    onClick={toggleSortOrder}
                    className="ml-2 text-sm p-3 bg-blue-500 text-white rounded-md flex items-center"
                  >
                    Sort
                    {sortOrder === "asc" ? (
                      <FontAwesomeIcon icon={faChevronUp} className="ml-2" />
                    ) : (
                      <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
                    )}
                  </button>
                </div>
              </div>
              {newMessages.length > 0 ? (
                <div>
                  <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-4">
                    {sortMessages()?.map((message) => {
                      return (
                        <li
                          key={message.id}
                          onClick={() =>
                            viewMessage(
                              message.client_id,
                              message.reference,
                              message.name,
                              message.client_id,
                            )
                          }
                          className="hover:bg-slate-100 cursor-pointer"
                        >
                          <div className="border-b py-2">
                            <div className="flex items-center justify-between">
                              <div>{message.name}</div>
                              <div>
                                {moment(message.created_at).format("DD/MM/YYYY")}
                              </div>
                            </div>
                            <p className="text-xs">{message.body}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-500">No New Messages</p>
                </div>
              )}
            </article>

            <article className="p-4 bg-white flex-1 rounded-md shadow-md">
              <div className="md:flex items-center justify-between">
                <h3 className="text-base md:text-lg lg:text-xl font-medium mb-4 flex items-center">
                  <p>Updates</p>
                </h3>
                <div className="text-xs md:text-sm lg:text-base border border-gray-300 rounded-lg p-0.5">
                  <Datepicker
                    primaryColor={"blue"}
                    value={updateDay}
                    onChange={handleUpdateChange}
                    useRange={false}
                  />
                </div>
              </div>
              {(updates ?? []).length > 0 ? (
                <div>
                  <ul className="max-h-[250px] min-h-[136px] overflow-y-scroll mt-4">
                    {updates?.map((update) => {
                      return (
                        <li
                          key={update.id}
                          onClick={() => handleClick(update)}
                          className="hover:bg-slate-100 cursor-pointer"
                        >
                          <div className="border-b py-2">
                            <div className="flex items-center justify-between">
                              <div>{update.title}</div>
                              <div>
                                {moment(update.created_at).format("DD/MM/YYYY")}
                              </div>
                            </div>
                            <p className="text-xs">{update.body}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-500">
                    No updates in the last 7 days
                  </p>
                </div>
              )}
            </article>
          </>
        )}
      </section>
      <NotificationSlideOut
        open={open}
        setOpen={setOpen}
        message={selectedThread}
      />
    </section>
  );
}

export default Dashboard;
